import React, { memo, useMemo, useRef } from 'react';
import { FlatList, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useRoute } from '@react-navigation/native';

import { useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { EventInfo, EventInfoWithoutPlayers } from '@/api/events/types/types';
import { Box } from '@/components/lib/components';
import { useScaleCarouselItemMountAnimation } from '@/feature/circular-pager/hooks/use-scale-carousel-item-mount-animation';
import { useStartAutoScroll } from '@/feature/circular-pager/hooks/use-start-auto-scroll';
import { PickemEventDetailsCard } from '@/feature/lobby/components/PickemEventCard';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { common } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { MAX_WEB_WIDTH, isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { sortEvents } from '@/utils/sortEvents';

import { useFullScreenPagerTransition } from '../../circular-pager/FullScreenPagerTransitionProvider';
import { FantasyEventsSheetsParamsList } from '../FantasyEventsSheets';
import { useSinglePlayerProfile } from '../SinglePlayerProfileProvider';
import { AUTO_SCROLL_LIST_ENABLED, AutoScrollingListLoop, AutoScrollingListLoopRef } from '../auto-scrolling-list';

export const EVENT_CAROUSEL_HEIGHT = 106;
const PGA_EVENT_CAROUSEL_HEIGHT = 64;
// for tennis we also display the tournament location in the event card
const TENNIS_EVENT_CAROUSEL_HEIGHT = 128;

const EVENTS_CAROUSEL_HEIGHT_BY_LEAGUE: Partial<Record<League, number>> = {
    [League.Pga]: PGA_EVENT_CAROUSEL_HEIGHT,
    [League.Atp]: TENNIS_EVENT_CAROUSEL_HEIGHT,
    [League.Wta]: TENNIS_EVENT_CAROUSEL_HEIGHT,
};

export const getEventCarouselHeight = (league: League) =>
    EVENTS_CAROUSEL_HEIGHT_BY_LEAGUE[league] ?? EVENT_CAROUSEL_HEIGHT;

const AUTO_SCROLL_LIST_SPACING = 12;

/**
 * Component that renders the events tab bar as a carousel
 * when it loads, it kicks off an animation using the sheetMountAnimationProgress value.
 * after that, it uses the animatedIndex value for ongoing animations.
 */
export const EventsCarouselTabBar = memo(() => {
    const { animatedIndex, sheetMountAnimationProgress, isSheetMountCompleted } = useFullScreenPagerTransition();

    const mountingStyles = useAnimatedStyle(() => {
        return {
            opacity: interpolate(sheetMountAnimationProgress.value, [-1, -0.5, 0], [0, 1, 1]),
        };
    });

    const bottomSheetGestureStyles = useAnimatedStyle(() => {
        if (!isSheetMountCompleted.value) {
            return {};
        }

        return {
            opacity: interpolate(animatedIndex.value, [-1, 0, 1], [0, 1, 0]),
        };
    });

    const { playerProfileAnimatedIndex, isPlayerProfileModalOpen } = useSinglePlayerProfile();

    // change the zIndex of the carousel when the modal is fully open,
    // this way the carousel will be behind the modal
    const zIndexStyles = useAnimatedStyle(() => {
        return {
            opacity:
                isPlayerProfileModalOpen.value && animatedIndex.value > 0
                    ? 0
                    : interpolate(playerProfileAnimatedIndex.value, [0, 1], [1, 0], Extrapolation.CLAMP),
            // if the single player profile is open -> we check if it's fully open or if the event sheet is fully open and hide the events carousel
            // if the event sheet is open -> we check if it's fully open and hide the events carousel
            // if the event sheet is closed -> we show the events carousel
            // if the single player profile is closed -> we show the events carousel
            zIndex: isPlayerProfileModalOpen.value
                ? playerProfileAnimatedIndex.value > 0 || animatedIndex.value > 0
                    ? -1
                    : 1
                : animatedIndex.value > 0
                ? -1
                : 1,
        };
    });

    const position = isWeb ? undefined : common.absolute;

    return (
        <Animated.View style={[position, zIndexStyles, mountingStyles, bottomSheetGestureStyles]}>
            <EventCarouselTabBarContents />
        </Animated.View>
    );
});

export const EventCarouselTabBarContents = () => {
    const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();

    const [{ data }] = useLeagueUpcomingEventsQuery({
        variables: { league: params.league },
        // query is already called in the parent component
        // so we set the requestPolicy to `cache-only`
        requestPolicy: 'cache-only',
    });

    const events: (EventInfo | EventInfoWithoutPlayers)[] = useMemo(
        () => (data?.getUpcomingEventsV2 || []).toSorted(sortEvents),
        [data?.getUpcomingEventsV2]
    );

    const insets = useSafeAreaInsets();
    const { width } = useWindowDimensions();

    const autoScrollingListRef = useRef<AutoScrollingListLoopRef>(null);

    useStartAutoScroll(autoScrollingListRef, AUTO_SCROLL_LIST_ENABLED);

    // we need this only for the initial render of the carousel
    // so we use useMemo with an empty array to avoid re-computing it on every render
    const initialIndex = useMemo(() => useEventsTabsStore.getState().selectedTab, []);

    return (
        <Box
            style={{ marginTop: insets.top }}
            paddingTop={'s12'}
            zIndex={1}
            height={getEventCarouselHeight(params.league)}
            width={width}
            maxWidth={isWeb ? MAX_WEB_WIDTH : width}
        >
            {AUTO_SCROLL_LIST_ENABLED ? (
                <AutoScrollingListLoop
                    ref={autoScrollingListRef}
                    style={common.justifyCenter}
                    spacing={AUTO_SCROLL_LIST_SPACING}
                    frameRate={60}
                    speed={0.2}
                    data={events}
                    initialIndex={initialIndex}
                    renderItem={({ item, index }) => <EventCarouselItem event={item} index={index % events.length} />}
                />
            ) : (
                <FlatList
                    contentContainerStyle={styles.list}
                    data={events}
                    horizontal
                    keyExtractor={item => item.id}
                    renderItem={({ item, index }) => <EventCarouselItem event={item} index={index % events?.length} />}
                    showsHorizontalScrollIndicator={showScrollIndicator}
                />
            )}
        </Box>
    );
};

const EventCarouselItem = ({ event, index }: { event: EventInfo | EventInfoWithoutPlayers; index: number }) => {
    // ! the `isSelected` logic *needs* to be inside the "Item" component to avoid flickers
    const isSelected = useEventsTabsStore(state => state.selectedTab === index);

    const animatedStyles = useScaleCarouselItemMountAnimation();

    return (
        <Animated.View style={animatedStyles}>
            <PickemEventDetailsCard index={index} pickemEvent={event} variant="eventDetails" isSelected={isSelected} />
        </Animated.View>
    );
};

export const styles = StyleSheet.create({
    list: {
        gap: AUTO_SCROLL_LIST_SPACING,
        justifyContent: 'center',
        flexGrow: 1,
    },
});
